import { CountUp } from "countup.js";

declare global {
  interface Window {
    WMUCountUp?: {
      boot(root?: ParentNode | HTMLElement | Document | null): void;
      countTo(element: HTMLElement, targetValue?: number | null, options?: CountToOptions): boolean;
      animateElement(element: HTMLElement, options?: CountToOptions): boolean;
      formatValue(value: number, options?: CountToOptions): string;
    };
  }
}

export type CountToOptions = {
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
  useGrouping?: boolean;
  separator?: string;
  startVal?: number;
};

export type WMUCountUpAPI = {
  boot: (root?: ParentNode | HTMLElement | Document | null) => void;
  countTo: (element: HTMLElement, targetValue?: number | null, options?: CountToOptions) => boolean;
  animateElement: (element: HTMLElement, options?: CountToOptions) => boolean;
  formatValue: (value: number, options?: CountToOptions) => string;
};

const countupInstances = new WeakMap<HTMLElement, CountUp>();
const ANNOUNCER_ID = "countup-announcer";

function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

function parseNumber(raw: string | number | null | undefined): number | null {
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }
  const cleaned = String(raw ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/[^0-9+.-]/g, "");
  if (!cleaned || cleaned === "+" || cleaned === "-" || cleaned === "." || cleaned === "+." || cleaned === "-.") {
    return null;
  }
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

function parseBoolean(raw: string | null | undefined, fallback: boolean): boolean {
  if (raw == null || raw === "") return fallback;
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === "false" || normalized === "0" || normalized === "off" || normalized === "no") return false;
  if (normalized === "true" || normalized === "1" || normalized === "on" || normalized === "yes") return true;
  return fallback;
}

function resolveElementOptions(element: HTMLElement, overrides?: CountToOptions): CountToOptions {
  const attrDecimals = Number.parseInt(String(element.getAttribute("data-count-decimals") || ""), 10);
  const attrDuration = Number.parseInt(String(element.getAttribute("data-count-duration") || ""), 10);
  const attrGrouping = element.getAttribute("data-count-grouping");
  const attrSeparator = element.getAttribute("data-count-separator");
  return {
    prefix: overrides?.prefix ?? String(element.getAttribute("data-count-prefix") || ""),
    suffix: overrides?.suffix ?? String(element.getAttribute("data-count-suffix") || ""),
    decimals: Number.isFinite(overrides?.decimals) ? Number(overrides?.decimals) : (Number.isFinite(attrDecimals) ? attrDecimals : 0),
    duration: Number.isFinite(overrides?.duration) ? Number(overrides?.duration) : (Number.isFinite(attrDuration) ? attrDuration : 900),
    useGrouping: overrides?.useGrouping ?? parseBoolean(attrGrouping, true),
    separator: overrides?.separator ?? String(attrSeparator || ","),
    startVal: Number.isFinite(overrides?.startVal) ? Number(overrides?.startVal) : undefined
  };
}

function formatValue(value: number, options?: CountToOptions): string {
  const settings = options || {};
  const decimals = Number.isFinite(settings.decimals) ? Number(settings.decimals) : 0;
  const useGrouping = settings.useGrouping !== false;
  const formatter = new Intl.NumberFormat("en-US", {
    useGrouping,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  let output = formatter.format(value);
  if (!useGrouping && settings.separator === "") {
    output = value.toFixed(decimals);
  }
  return `${settings.prefix || ""}${output}${settings.suffix || ""}`;
}

function resolveTargetValue(element: HTMLElement, explicitTarget?: number | null): number | null {
  if (Number.isFinite(explicitTarget)) {
    return Number(explicitTarget);
  }
  const attrTarget = element.getAttribute("data-countup")
    || element.getAttribute("data-count-to")
    || element.getAttribute("data-count");
  return parseNumber(attrTarget);
}

function resolveStartValue(element: HTMLElement, options?: CountToOptions): number {
  const optionStart = parseNumber(options?.startVal ?? null);
  if (optionStart != null) return optionStart;
  const datasetStart = parseNumber(element.dataset.countCurrent);
  if (datasetStart != null) return datasetStart;
  const currentText = parseNumber(element.textContent);
  return currentText ?? 0;
}

function renderImmediate(element: HTMLElement, targetValue: number, options?: CountToOptions): void {
  element.textContent = formatValue(targetValue, options);
  element.dataset.countCurrent = String(targetValue);
  element.dataset.countTarget = String(targetValue);
  element.dataset.countAnimated = "1";
  announceValue(formatValue(targetValue, options));
}

function ensureAnnouncer(): HTMLElement {
  const existing = document.getElementById(ANNOUNCER_ID);
  if (existing instanceof HTMLElement) {
    return existing;
  }
  const announcer = document.createElement("div");
  announcer.id = ANNOUNCER_ID;
  announcer.className = "sr-only";
  announcer.setAttribute("aria-live", "polite");
  announcer.setAttribute("aria-atomic", "true");
  document.body.appendChild(announcer);
  return announcer;
}

function announceValue(value: string): void {
  const announcer = ensureAnnouncer();
  announcer.textContent = "";
  window.setTimeout(() => {
    announcer.textContent = value;
  }, 0);
}

function countTo(element: HTMLElement, targetValue?: number | null, options?: CountToOptions): boolean {
  if (!(element instanceof HTMLElement)) return false;
  const target = resolveTargetValue(element, targetValue);
  if (target == null) return false;

  const settings = resolveElementOptions(element, options);
  const currentTarget = parseNumber(element.dataset.countTarget);
  if (currentTarget === target && element.dataset.countAnimated === "1") {
    return true;
  }

  if (prefersReducedMotion()) {
    renderImmediate(element, target, settings);
    return true;
  }

  const startValue = resolveStartValue(element, settings);
  const existing = countupInstances.get(element);
  if (existing) {
    element.dataset.countCurrent = String(startValue);
    element.dataset.countTarget = String(target);
    existing.update(target);
    element.dataset.countAnimated = "1";
    return true;
  }

  const counter = new CountUp(element, target, {
    startVal: startValue,
    duration: (Number.isFinite(settings.duration) ? Number(settings.duration) : 900) / 1000,
    decimalPlaces: Number.isFinite(settings.decimals) ? Number(settings.decimals) : 0,
    prefix: settings.prefix || "",
    suffix: settings.suffix || "",
    separator: settings.separator || ",",
    useGrouping: settings.useGrouping !== false,
    useEasing: true,
    smartEasingThreshold: 999,
    smartEasingAmount: 333,
    formattingFn: (value: number) => formatValue(value, settings)
  });

  if (counter.error) {
    renderImmediate(element, target, settings);
    return false;
  }

  countupInstances.set(element, counter);
  element.dataset.countCurrent = String(startValue);
  element.dataset.countTarget = String(target);
  counter.start(() => {
    element.dataset.countCurrent = String(target);
    element.dataset.countAnimated = "1";
    announceValue(formatValue(target, settings));
  });
  return true;
}

function animateElement(element: HTMLElement, options?: CountToOptions): boolean {
  return countTo(element, undefined, options);
}

function collectAnimatedElements(root?: ParentNode | HTMLElement | Document | null): HTMLElement[] {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  const elements: HTMLElement[] = [];
  if (scope instanceof HTMLElement && scope.matches("[data-countup], [data-count-to], [data-count]")) {
    elements.push(scope);
  }
  scope.querySelectorAll<HTMLElement>("[data-countup], [data-count-to], [data-count]").forEach((element) => {
    elements.push(element);
  });
  return elements;
}

function boot(root?: ParentNode | HTMLElement | Document | null): void {
  ensureAnnouncer();
  collectAnimatedElements(root).forEach((element) => {
    animateElement(element);
  });
}

window.WMUCountUp = {
  boot,
  countTo,
  animateElement,
  formatValue
} satisfies WMUCountUpAPI;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => boot(document));
} else {
  boot(document);
}

document.addEventListener("htmx:afterSettle", (event) => {
  const target = event && "target" in event ? (event.target as EventTarget | null) : null;
  boot(target instanceof HTMLElement ? target : document);
});
